import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import { Stack, Button, Container, Typography } from '@mui/material';

import useBrandName from 'src/hooks/use-brand-name';

import { AGENT, TRYVISA } from 'src/_config/brandConfig';

import Image from 'src/components/image';

import { useAuthContext } from 'src/auth/hooks';

export default function RedirectWithTimer({ redirectType = 'agent' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLocalDevelopment } = useBrandName();
  const { logout } = useAuthContext();
  const [countdown, setCountdown] = useState(10);

  const getRedirectUrl = () => {
    if (redirectType === 'tryvisa') {
      return isLocalDevelopment
        ? `http://${window.location.hostname}:${TRYVISA.port}`
        : TRYVISA.dashboardUrl;
    }
    return isLocalDevelopment
      ? `http://${window.location.hostname}:${AGENT.port}`
      : AGENT.dashboardUrl;
  };

  const redirectUrl = getRedirectUrl();

  useEffect(() => {
    // Check if we need to immediately logout based on URL parameters
    const url = new URL(window.location.href);
    const email = url.searchParams.get('email');
    const type = url.searchParams.get('type');
    const previousPath = url.searchParams.get('previous_path');

    const isLoginWithAgent =
      url.pathname.includes('/auth/register') &&
      email &&
      type === 'AGENT' &&
      previousPath &&
      decodeURIComponent(previousPath).includes('tryvisa.com');

    if (isLoginWithAgent) {
      logout();
      return undefined;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          window.history.replaceState(null, '', '/');
          window.location.replace(redirectUrl);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [redirectUrl, isLocalDevelopment, logout]);

  const handleManualRedirect = (isLogout = false) => {
    window.history.replaceState(null, '', '/');
    if (isLogout) {
      logout();
      window.location.replace('/');
    } else {
      window.location.replace(redirectUrl);
    }
  };

  const getBannerImage = () => {
    if (redirectType === 'tryvisa') {
      return '/assets/images/banners/tryvisa-banner.png';
    }
    return '/assets/images/banners/agent-banner.png';
  };

  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
      }}
    >
      <Image src={getBannerImage()} sx={{ mb: -1, width: 1 }} />
      <Typography textAlign="center" variant="h4" gutterBottom>
        Redirecting to {redirectUrl} in {countdown} seconds...
      </Typography>
      <Stack direction="row" spacing={2}>
        <Button variant="contained" color="primary" onClick={() => handleManualRedirect(false)}>
          Go Now
        </Button>
        <Button variant="outlined" color="error" onClick={() => handleManualRedirect(true)}>
          Logout
        </Button>
      </Stack>
    </Container>
  );
}

RedirectWithTimer.propTypes = {
  redirectType: PropTypes.oneOf(['agent', 'tryvisa']),
};
